import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import moment from 'moment-timezone'
import {
  FeatherLoader,
  ModelContext,
  useNotificationService,
  useUserService,
} from '@abroad/components'
import { formatDateTimeWithTimezone } from '../../utils/date'
import TruncatedText from '../OrgEco/TruncatedText'
import OverflowText from './OverflowText'
import AddSessionModal from '../clientProfile/AddSessionModal'
import SchedulingListTitle from './components/SchedulingListTitle'

// Event and coach names longer than this are cut off, the full text shows on hover
const TEXT_MAX_LENGTH = 25

const BookingListView = ({
  selectedStatus,
  onStatusChange,
  schedulingList,
  isLoading,
  isLoadingMore = false,
  onRescheduleClick,
  onCancelClick,
  hasMore = false,
  onLoadMore,
  timezone,
  clearFilter = null,
  highlightDate,
  highlightEventIds,
  onHighlightClear,
  isWithin24Hours = null, // Function to check if booking is within 24 hours
  isComeFromClientSessions = false, // Flag to show description column instead of client name
  isCalendarConnected = true, // Flag to indicate if calendar is connected
  showCoachColumn = false, // Flag to show the booking's coach (all-coaches client view)
  sort = null, // { sortBy, sortOrder } of the list, used with onSortChange
  onSortChange = null, // Makes the Coach and Date headers clickable when provided
}) => {
  const observer = useRef(null)
  const loader = useRef(null)
  const isNotProduction = process.env.REACT_APP_ENV !== 'prod'
  const [highlightedRows, setHighlightedRows] = useState(new Set())
  const highlightTimeoutRef = useRef(null)
  const { user } = useUserService()
  const Notification = useNotificationService()
  const [logSessionBooking, setLogSessionBooking] = useState(null)

  const handleAddLogSession = (booking) => {
    if (!booking?.clientId || !booking?.sowId) {
      Notification.showNotification(
        'Unable to open log session. Client or SOW is missing for this booking.',
        'danger',
      )
      return
    }
    setLogSessionBooking(booking)
  }

  const closeLogSessionModal = () => {
    setLogSessionBooking(null)
  }

  // In the all-coaches list a coach can only act on their own bookings
  const canManageBooking = (booking) =>
    !showCoachColumn || booking?.coachId === user?._id

  // The action buttons only show for manageable bookings, so the column stays narrow without any
  const hasManageableBooking = schedulingList.some((booking) =>
    canManageBooking(booking),
  )

  // Header that toggles the sort on click, with a direction arrow like the experience list
  // `align` matches the column's body cells: 'left' for text, 'center' for times and actions
  const renderSortableHeader = (
    label,
    sortKey,
    align = 'center',
    widthClass = '',
  ) => {
    const alignClass = `${
      align === 'left' ? 'text-left' : 'text-center'
    } ${widthClass}`.trim()
    if (!onSortChange) {
      return <th className={alignClass}>{label}</th>
    }
    const isActive = sort?.sortBy === sortKey
    const isDesc = isActive && sort?.sortOrder === 'desc'
    return (
      <th
        className={alignClass}
        aria-sort={isActive ? (isDesc ? 'descending' : 'ascending') : 'none'}>
        <button
          type='button'
          className={`scheduling-sort-btn scheduling-sort-btn--${align}`}
          onClick={() => onSortChange(sortKey)}>
          {label}
          <svg
            className={`scheduling-sort-icon${isActive ? ' is-active' : ''}`}
            width='10'
            height='10'
            viewBox='0 0 10 10'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.6'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'>
            <path d={isDesc ? 'M2 3.5L5 6.5L8 3.5' : 'M2 6.5L5 3.5L8 6.5'} />
          </svg>
        </button>
      </th>
    )
  }

  // Calculate colSpan based on visible columns
  const getColSpan = () => {
    let cols = 1 // #
    if (!isComeFromClientSessions) cols += 1 // Client Name
    cols += 1 // Event
    if (showCoachColumn) cols += 1 // Coach
    cols += 1 // Date
    cols += 1 // Time
    if (isNotProduction) cols += 1 // Duration
    if (user?.isCoach) cols += 1 // Actions
    return cols
  }

  // Handle highlighting when highlightDate or highlightEventIds change
  useEffect(() => {
    if (
      !highlightDate &&
      (!highlightEventIds || highlightEventIds.length === 0)
    ) {
      return
    }

    // Clear any existing timeout
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current)
    }

    // Find matching rows
    const matchingRowIds = new Set()
    schedulingList.forEach((booking) => {
      const bookingId = booking?._id || booking?.id
      const bookingDate = booking?.startDateTime

      // Check if booking matches highlight date (compare in timezone)
      if (highlightDate && bookingDate && timezone) {
        const bookingDateInTimezone = moment
          .utc(bookingDate)
          .tz(timezone)
          .format('YYYY-MM-DD')
        if (bookingDateInTimezone === highlightDate) {
          matchingRowIds.add(bookingId)
        }
      } else if (highlightDate && bookingDate) {
        // Fallback to UTC comparison if no timezone
        const bookingDateStr = moment.utc(bookingDate).format('YYYY-MM-DD')
        if (bookingDateStr === highlightDate) {
          matchingRowIds.add(bookingId)
        }
      }

      // Check if booking matches highlight event IDs
      if (highlightEventIds && highlightEventIds.length > 0 && bookingId) {
        if (highlightEventIds.includes(bookingId)) {
          matchingRowIds.add(bookingId)
        }
      }
    })

    // Set highlighted rows
    if (matchingRowIds.size > 0) {
      setHighlightedRows(matchingRowIds)

      // Clear highlight after 3 seconds
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedRows(new Set())
        if (onHighlightClear) {
          onHighlightClear()
        }
      }, 3000)
    }

    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current)
      }
    }
  }, [highlightDate, highlightEventIds, schedulingList, onHighlightClear])

  const handleObserver = useCallback(
    (entities) => {
      const target = entities[0]
      if (
        target.isIntersecting &&
        hasMore &&
        !isLoading &&
        !isLoadingMore &&
        onLoadMore
      ) {
        onLoadMore()
      }
    },
    [hasMore, isLoading, isLoadingMore, onLoadMore],
  )

  useEffect(() => {
    // Simple logic: If hasMore is true, set up observer to watch loader element
    if (hasMore && !isLoading && !isLoadingMore) {
      const options = {
        root: null,
        rootMargin: '20px',
        threshold: 1.0,
      }
      observer.current = new IntersectionObserver(handleObserver, options)
      if (loader.current && observer.current) {
        observer.current.observe(loader.current)
      }
    } else {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoading, isLoadingMore, handleObserver])

  const renderTableRows = () =>
    schedulingList.map((booking, index) => {
      const rowKey = booking?._id || booking?.id || `${index}`
      const clientName = booking?.clientName || '-'
      const clientId = booking?.clientId || null
      const eventTitle = booking?.eventTitle || '-'
      const { date: eventDate, time: eventTime } = formatDateTimeWithTimezone(
        booking?.startDateTime,
        timezone,
      )
      const isHighlighted = highlightedRows.has(rowKey)

      return (
        <tr
          key={rowKey}
          style={
            isHighlighted
              ? {
                  backgroundColor: '#fff3cd',
                  transition: 'background-color 0.3s ease',
                }
              : {}
          }>
          <td>{index + 1}</td>
          {!isComeFromClientSessions && (
            <td className='font-weight-medium'>
              {clientId ? (
                <NavLink
                  to={`/coach/clients/${clientId}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-dark-600'>
                  {clientName}
                </NavLink>
              ) : (
                clientName
              )}
            </td>
          )}
          <td>
            <OverflowText text={eventTitle} cursor='default' />
          </td>
          {showCoachColumn && (
            <td className='text-truncate' style={{ maxWidth: 200 }}>
              <TruncatedText
                text={booking?.coachName || '-'}
                max={TEXT_MAX_LENGTH}
                cursor='default'
              />
            </td>
          )}
          <td className='text-center'>{eventDate}</td>
          <td className='text-center'>{eventTime}</td>
          {isNotProduction && (
            <td className='text-center'>{booking?.duration} Minutes</td>
          )}
          {user?.isCoach && !canManageBooking(booking) && (
            <td className='text-center'>-</td>
          )}
          {user?.isCoach && canManageBooking(booking) && (
            <td className='text-center text-nowrap'>
              {booking?.clientId && booking?.sowId && (
                <>
                  <span
                    className='text-saffron-700 cursor-pointer'
                    onClick={() => handleAddLogSession(booking)}>
                    Add A Session
                  </span>
                  <span className='archive-border-right mx-2'></span>
                </>
              )}
              {(() => {
                const within24Hours = isWithin24Hours
                  ? isWithin24Hours(booking)
                  : false
                const isRescheduleDisabled = within24Hours

                if (isRescheduleDisabled) {
                  return (
                    <OverlayTrigger
                      placement='top'
                      overlay={
                        <Tooltip
                          style={{
                            '--bs-tooltip-max-width': '22rem',
                            maxWidth: '22rem',
                          }}
                          id={`tooltip-reschedule-${index}`}
                          className='font-open-sans font-16 font-semibold leading-7 text-center schedule-info'>
                          <div
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              whiteSpace: 'normal',
                            }}>
                            You cannot reschedule or cancel this session within
                            24 hours of its start time.
                          </div>
                        </Tooltip>
                      }>
                      <span className='d-inline-block'>
                        <span
                          className='text-muted'
                          style={{
                            cursor: 'not-allowed',
                            opacity: 0.6,
                          }}>
                          Rescheduling
                        </span>
                      </span>
                    </OverlayTrigger>
                  )
                }

                return (
                  <span
                    className='text-saffron-700 cursor-pointer'
                    onClick={() => onRescheduleClick(booking)}>
                    Rescheduling
                  </span>
                )
              })()}
              <span className='archive-border-right mx-2'></span>
              {(() => {
                const within24Hours = isWithin24Hours
                  ? isWithin24Hours(booking)
                  : false

                if (within24Hours) {
                  return (
                    <OverlayTrigger
                      placement='top'
                      overlay={
                        <Tooltip
                          style={{
                            '--bs-tooltip-max-width': '22rem',
                            maxWidth: '22rem',
                          }}
                          id={`tooltip-cancel-${index}`}
                          className='font-open-sans font-16 font-semibold leading-7 text-center schedule-info'>
                          <div
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              whiteSpace: 'normal',
                            }}>
                            You cannot reschedule or cancel this session within
                            24 hours of its start time.
                          </div>
                        </Tooltip>
                      }>
                      <span className='d-inline-block'>
                        <span
                          className='text-danger'
                          style={{
                            cursor: 'not-allowed',
                            opacity: 0.6,
                          }}>
                          Cancel
                        </span>
                      </span>
                    </OverlayTrigger>
                  )
                }

                return (
                  <span
                    className='text-danger cursor-pointer'
                    onClick={() => onCancelClick && onCancelClick(booking)}>
                    Cancel
                  </span>
                )
              })()}
            </td>
          )}
        </tr>
      )
    })

  return (
    <>
      <section className='scheduling-list-section'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <SchedulingListTitle
            title='Scheduled Lists'
            showPreview={
              !isComeFromClientSessions && isCalendarConnected && !!timezone
            }
            timezone={timezone}
          />
          {clearFilter}
        </div>
        <Table responsive className='custom-table scheduling-list-table'>
          <thead>
            <tr>
              <th>#</th>
              {!isComeFromClientSessions && (
                <th className='scheduling-client-col'>Client Name</th>
              )}
              <th className='text-left'>Event</th>
              {showCoachColumn &&
                renderSortableHeader(
                  'Coach',
                  'coach',
                  'left',
                  'scheduling-coach-col',
                )}
              {renderSortableHeader(
                'Date',
                'date',
                'center',
                'scheduling-date-col',
              )}
              <th className='text-center scheduling-time-col'>Time</th>
              {isNotProduction && (
                <th className='text-center scheduling-duration-col'>
                  Duration
                </th>
              )}
              {user?.isCoach && (
                <th
                  className={`text-uppercase sa2 text-center text-nowrap scheduling-actions-col${
                    hasManageableBooking ? ' scheduling-actions-col--wide' : ''
                  }`}>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          {isLoading && schedulingList.length === 0 && (
            <tr>
              <td className='text-center border-0 pt-3' colSpan={getColSpan()}>
                <FeatherLoader />
              </td>
            </tr>
          )}
          {schedulingList.length === 0 && !isLoading && (
            <tr>
              <td className='text-center s6 py-3' colSpan={getColSpan()}>
                {!isCalendarConnected && timezone
                  ? 'Please connect your calendar first to view scheduled sessions.'
                  : 'No bookings found.'}
              </td>
            </tr>
          )}
          <tbody>
            {schedulingList?.length > 0 && !isLoading && renderTableRows()}
            {isLoading && schedulingList.length > 0 && (
              <tr>
                <td colSpan={getColSpan()} className='text-center py-3'>
                  <FeatherLoader />
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr ref={loader}>
              <td colSpan={getColSpan()} className='text-center py-2'>
                {isLoadingMore && <FeatherLoader />}
              </td>
            </tr>
          </tfoot>
        </Table>
      </section>
      <ModelContext.Provider
        value={{
          show: !!logSessionBooking,
          onHide: closeLogSessionModal,
        }}>
        {logSessionBooking && (
          <AddSessionModal
            modalTitle='Add Log Session'
            overrideClientId={logSessionBooking.clientId}
            scheduledEventId={logSessionBooking.scheduleEventId || null}
            scheduledEvent={{
              _id: logSessionBooking.scheduleEventId || null,
              title:
                logSessionBooking.eventTitle ||
                logSessionBooking.eventName ||
                '',
              startDateTime: logSessionBooking.startDateTime || null,
            }}
            prefilledPlan={{
              _id: logSessionBooking.sowId,
              planName: logSessionBooking.planName || '',
              sessionDuration: logSessionBooking.duration || null,
              includeGrowthSession: !!logSessionBooking.includeGrowthSession,
              sowType: logSessionBooking.sowType || 'REGULAR',
            }}
          />
        )}
      </ModelContext.Provider>
    </>
  )
}

export default BookingListView
